import React, { useState, FC, useMemo, useEffect, useRef, useCallback } from 'react';
import cn from 'classnames';
import { Slider } from 'antd';
import { ButtonStroke } from '@blink/components';
import { useFormContext } from 'react-hook-form';

import { useLeverageFactorStyle } from './LeverageFactor.style';
import { BorrowAmountFields } from '../../BorrowAmount';
import { RiskFactorFields } from '../RiskFactor';

import { trimDecimals } from '@/shared/utils/helpers';
import { LeverageStrategy } from '@/shared/validations/borrowModal.schema';
import { SelectedBorrowAsset } from '@/features/Modals';
import { useThrottleCallback } from '@/shared/hooks/useThrottleCallback';

type LeverageFactorProps = {
    collateralMarketValue: string | null;
    selectedAssets: SelectedBorrowAsset[];
    onChange: (value: number) => void;
    decimals: number;
};

const MIN_LEVERAGE = 1;
const MAX_LEVERAGE = 5;
const STEP = 0.1;
const PREDEFINED_LEVERAGES = [1, 2, 3.5, 5];

const LeverageFactor: FC<LeverageFactorProps> = ({
    onChange,
    collateralMarketValue,
    decimals,
    selectedAssets,
}) => {
    const classes = useLeverageFactorStyle();

    const { setValue, watch, trigger } = useFormContext();

    const [percent, setPercent] = useState(MIN_LEVERAGE);
    const [activePredefined, setActivePredefined] = useState<number | null>(1);

    const strategy = watch(RiskFactorFields.STRATEGY);
    const leverageAmount = watch(BorrowAmountFields.ASSET_AMOUNT);

    const previousSelectedAssetsRef = useRef<SelectedBorrowAsset[] | null>(null);
    const previousLeverageAmount = useRef<string | null>(leverageAmount);

    useEffect(() => {
        const hasChanged =
            !previousSelectedAssetsRef.current ||
            !areAssetsEqual(previousSelectedAssetsRef.current, selectedAssets) ||
            previousLeverageAmount.current !== leverageAmount;

        if (strategy === LeverageStrategy.MANUAL && hasChanged) {
            setPercent(MIN_LEVERAGE);
            setActivePredefined(null);
            onChange(MIN_LEVERAGE);
        }

        previousSelectedAssetsRef.current = selectedAssets;
        previousLeverageAmount.current = leverageAmount;
    }, [strategy, selectedAssets, leverageAmount]);

    const getDesiredLeverageAmount = useCallback(
        (multiplier: number) => {
            return trimDecimals((Number(collateralMarketValue) * multiplier).toString(), decimals);
        },
        [collateralMarketValue, decimals],
    );

    const handleIncrease = () => {
        setValue(RiskFactorFields.STRATEGY, LeverageStrategy.PREDEFINED_WITH_PERCENT);

        setPercent((prev) => {
            const newPercent = Math.min(MAX_LEVERAGE, parseFloat((prev + STEP).toFixed(1)));

            setActivePredefined(null);
            onChange(newPercent);
            setValue(BorrowAmountFields.ASSET_AMOUNT, getDesiredLeverageAmount(newPercent));
            trigger(BorrowAmountFields.ASSET_AMOUNT);

            return newPercent;
        });
    };

    const handleDecrease = () => {
        setValue(RiskFactorFields.STRATEGY, LeverageStrategy.PREDEFINED_WITH_PERCENT);

        setPercent((prev) => {
            const newPercent = Math.max(MIN_LEVERAGE, parseFloat((prev - STEP).toFixed(1)));

            setActivePredefined(null);
            onChange(newPercent);
            setValue(BorrowAmountFields.ASSET_AMOUNT, getDesiredLeverageAmount(newPercent));
            trigger(BorrowAmountFields.ASSET_AMOUNT);

            return newPercent;
        });
    };

    const throttledSetLeverage = useThrottleCallback(async (value: number) => {
        setPercent(value);
        setActivePredefined(value);
        setValue(RiskFactorFields.STRATEGY, LeverageStrategy.PREDEFINED_WITH_PERCENT);
        setValue(BorrowAmountFields.ASSET_AMOUNT, getDesiredLeverageAmount(value));
        await trigger(BorrowAmountFields.ASSET_AMOUNT);
        onChange(value);
    });

    const handleReset = async () => {
        setPercent(MIN_LEVERAGE);
        setActivePredefined(null);
        onChange(MIN_LEVERAGE);
        setValue(RiskFactorFields.STRATEGY, LeverageStrategy.MANUAL);
        setValue(BorrowAmountFields.ASSET_AMOUNT, 0);
        await trigger(BorrowAmountFields.ASSET_AMOUNT);
    };

    const isDisabled = useMemo(() => !collateralMarketValue, [collateralMarketValue]);

    return (
        <>
            <div className={classes.leverageHeader}>
                <p>Leverage</p>
                <p className={classes.textBtn} onClick={handleReset}>
                    Reset
                </p>
            </div>

            <div className={classes.percentageWrapper}>
                <div className={classes.leveragePercentage}>
                    <ButtonStroke
                        onClick={handleDecrease}
                        disabled={percent === MIN_LEVERAGE || isDisabled}
                    >
                        -
                    </ButtonStroke>
                    <p>{percent}x</p>
                    <ButtonStroke
                        onClick={handleIncrease}
                        disabled={percent === MAX_LEVERAGE || isDisabled}
                    >
                        +
                    </ButtonStroke>
                </div>
                <div className={classes.leveragePercentageSlider}>
                    <Slider
                        value={percent}
                        min={MIN_LEVERAGE}
                        max={MAX_LEVERAGE}
                        step={STEP}
                        disabled={isDisabled}
                        onChange={(value) => {
                            setPercent(value);
                            setActivePredefined(null);
                        }}
                        onChangeComplete={(value) => {
                            onChange(value);
                            setValue(
                                RiskFactorFields.STRATEGY,
                                LeverageStrategy.PREDEFINED_WITH_PERCENT,
                            );
                            setValue(
                                BorrowAmountFields.ASSET_AMOUNT,
                                getDesiredLeverageAmount(value),
                            );
                            trigger(BorrowAmountFields.ASSET_AMOUNT);
                        }}
                        className={classes.slider}
                    />
                </div>
            </div>

            <div className={classes.leveragePredefinedValues}>
                {PREDEFINED_LEVERAGES.map((value) => (
                    <ButtonStroke
                        id={'values'}
                        key={value}
                        className={cn({
                            [classes.activeButton]: value === activePredefined,
                        })}
                        onClick={() => throttledSetLeverage(value)}
                        disabled={isDisabled}
                    >
                        {value}x
                    </ButtonStroke>
                ))}
            </div>
        </>
    );
};

function areAssetsEqual(a: SelectedBorrowAsset[], b: SelectedBorrowAsset[]) {
    if (a.length !== b.length) return false;

    return a.every((asset, index) => {
        const other = b[index];
        return (
            asset.symbol === other.symbol &&
            asset.address === other.address &&
            asset.amount === other.amount
        );
    });
}

export default LeverageFactor;
